import {useRef} from 'react';
import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useHostedZones} from '../hooks/useHostedZones';
import {useHostedZonesSearch} from '../hooks/useHostedZonesSearch';
import {useHostedZonesTableColumns} from '../hooks/useHostedZonesTableColumns';
import {HostedZone} from '../types';
import config from '../config';

export const RecordsTable = () => {

    const {columns} = useHostedZonesTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchRecords,
    } = useHostedZones();

    // Same module-scoped store useHostedZones filters on (FNP-815) — this
    // component owns the input, the list hook owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useHostedZonesSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // NO ROW SELECTION, deliberately. A hosted zone is read-only here: there is
    // no delete, no retire and no batch operation on the deployed schema for a
    // checkbox to apply to (writes are FNP-977). Adding one would be an
    // affordance with nothing behind it. Stated rather than left silent, because
    // "no selection" and "selection was forgotten" look identical in a diff.

    // The two empty states mean OPPOSITE things and `noRecordsText` is one
    // string, so the distinction is branched here rather than flattened into a
    // single message that sends an operator hunting for a bug that isn't there.
    const searching = keyword.trim() !== '';
    const noRecordsText = searching
        ? I18n.get('No loaded hosted zone matches this search')
        : I18n.get('No hosted zones found in this account');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load hosted zones')}>
                    {I18n.get('The domains API did not return the hosted zones. Try Reload; if it persists the Route 53 read may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<HostedZone>
                columns={columns}
                records={visibleItems}
                idAccessor={'id'}
                // Client-side sorting, over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing the term is what selects the filtered footer state —
                // omit it and a filtered view silently reports the unfiltered one.
                filterTerm={keyword}
                noun={'hosted zone'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
